import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  DASHBOARD_TOUR_STEPS,
  finishDashboardTour,
  placeTourTip,
  presentTourSteps,
  type TourRect,
  type TourStep,
  type TourStepId,
} from '@features/Dashboard';
import { trackEvent } from '@shared/analytics';
import { Button } from '@shared/ui';
import { useIsSidebarMobile } from '@widgets/Sidebar';

/** Отступ подсветки от края элемента. */
const PAD = 6;

interface Spot extends TourRect {
  radius: number;
}

/** Есть ли элемент шага в разметке: пункт боковой панели или вкладка внизу телефона. */
function hasTarget(id: TourStepId): boolean {
  return findTarget(id) !== null;
}

/** Первый видимый элемент шага (`data-tour`). */
function findTarget(id: TourStepId): HTMLElement | null {
  for (const node of document.querySelectorAll<HTMLElement>(`[data-tour="${id}"]`)) {
    const r = node.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return node;
  }
  return null;
}

function spotOf(el: HTMLElement): Spot {
  const r = el.getBoundingClientRect();
  const corner = parseFloat(getComputedStyle(el).borderTopLeftRadius) || 12;
  return {
    top: r.top - PAD,
    left: r.left - PAD,
    width: r.width + PAD * 2,
    height: r.height + PAD * 2,
    radius: Math.min(corner, r.height / 2) + PAD,
  };
}

interface DashboardTourProps {
  onDone: () => void;
}

/**
 * Знакомство с кабинетом после онбординга: затемнение с вырезом вокруг раздела навигации, окошко с текстом,
 * «Далее» / «Понятно» и «Пропустить». Страница под затемнением не нажимается. Шаги (решение владельца 2026-10-23):
 * «Главная», «Слова и модули», «Грамматика», «Игра» — пункты боковой панели на компьютере (окошко справа) и вкладки внизу на
 * телефоне (окошко над ними); M в «N из M» — по шагам, чей элемент нашёлся при старте.
 */
export function DashboardTour({ onDone }: DashboardTourProps) {
  // Шаги — после коммита: подсветка монтируется тем же рендером, что и главная, искать элементы можно только потом.
  const [steps, setSteps] = useState<TourStep[] | null>(null);
  useLayoutEffect(() => {
    setSteps(presentTourSteps(DASHBOARD_TOUR_STEPS, hasTarget));
  }, []);
  if (!steps) return null;
  if (!steps.length) return <TourEnd onDone={onDone} />;
  return <TourSteps steps={steps} onDone={onDone} />;
}

/** Ни одного элемента не нашлось — подсветку молча выключаем. */
function TourEnd({ onDone }: { onDone: () => void }) {
  useEffect(() => {
    finishDashboardTour();
    onDone();
  }, [onDone]);
  return null;
}

function TourSteps({ steps, onDone }: DashboardTourProps & { steps: TourStep[] }) {
  const { t } = useTranslation();
  const isMobile = useIsSidebarMobile();
  const titleId = useId();
  const bodyId = useId();
  const [index, setIndex] = useState(0);
  const [spot, setSpot] = useState<Spot | null>(null);
  const [tipPos, setTipPos] = useState<{ top: number; left: number } | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const step = steps[index];
  const total = steps.length;
  const last = index === total - 1;

  const finish = useCallback(
    (completed: boolean) => {
      finishDashboardTour();
      trackEvent('dashboard_tour_finished', { completed, step: step.id });
      onDone();
    },
    [onDone, step.id],
  );

  const next = useCallback(() => {
    if (last) finish(true);
    else setIndex((i) => i + 1);
  }, [finish, last]);
  // Таймер поиска элемента зовёт актуальный next, не перезапускаясь при каждой его смене.
  const nextRef = useRef(next);
  nextRef.current = next;

  useEffect(() => {
    trackEvent('dashboard_tour_step', { step: step.id, n: index + 1 });
  }, [step.id, index]);

  // Элемент шага и его место. Следим каждый кадр, а не по событиям: панель сворачивается анимацией, окно меняет
  // размер — по одному замеру рамка съезжала (замечание владельца 2026-10-19). Состояние меняется, только когда место
  // правда сдвинулось.
  useEffect(() => {
    let frame = 0;
    let last = '';
    const follow = () => {
      const el = findTarget(step.id);
      const next = el ? spotOf(el) : null;
      const key = next ? [next.top, next.left, next.width, next.height].map(Math.round).join(':') : '';
      if (key !== last) {
        last = key;
        setSpot(next);
      }
      frame = requestAnimationFrame(follow);
    };
    const timer = window.setTimeout(() => {
      // Элемента нет (пункт скрывает эта раскладка) — шаг пропускаем, а не показываем пустоту.
      if (!findTarget(step.id)) {
        nextRef.current();
        return;
      }
      follow();
    }, 0);
    return () => {
      window.clearTimeout(timer);
      cancelAnimationFrame(frame);
    };
  }, [step.id]);

  // Окошко — после того как известны место элемента и размер самого окошка.
  useLayoutEffect(() => {
    const tip = tipRef.current;
    if (!spot || !tip) {
      setTipPos(null);
      return;
    }
    const size = tip.getBoundingClientRect();
    setTipPos(
      placeTourTip(
        spot,
        { width: size.width, height: size.height },
        { width: window.innerWidth, height: window.innerHeight },
        isMobile ? 'below' : 'right',
      ),
    );
  }, [spot, isMobile, index]);

  // Фокус — на окошко, а не на «Далее»: программный фокус на кнопке Chrome рисовал рамкой, и после
  // нажатия мышью «Далее» выглядело подсвеченным (замечание владельца 2026-10-19). Рамка остаётся
  // для Tab; Enter и → листают, Esc закрывает.
  const shown = tipPos !== null;
  useEffect(() => {
    if (shown) tipRef.current?.focus({ preventScroll: true });
  }, [shown, index]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') finish(false);
      // На кнопке Enter нажмёт её сам — иначе «Пропустить» с клавиатуры листал бы вперёд.
      else if ((event.key === 'Enter' || event.key === 'ArrowRight') && event.target === tipRef.current) {
        event.preventDefault();
        next();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [finish, next]);

  // Заголовок шага — подпись самого раздела: на телефоне вкладка игры короче («Игра»).
  const titles: Record<TourStepId, string> = {
    home: t('nav.dashboard'),
    words: t('nav.words'),
    grammar: t('nav.grammar'),
    game: t(isMobile ? 'nav.game' : 'nav.stories'),
  };
  const title = titles[step.id];
  const body = t(`dashboard.tour.${step.id}`);

  return createPortal(
    <div className="fixed inset-0 z-[400]">
      {/* Перехват касаний мимо окошка: под подсветкой страница не нажимается. */}
      <div className="absolute inset-0" aria-hidden />
      {spot ? (
        <div
          aria-hidden
          className="pointer-events-none fixed transition-[top,left,width,height] duration-200 motion-reduce:transition-none"
          style={{
            top: spot.top,
            left: spot.left,
            width: spot.width,
            height: spot.height,
            borderRadius: spot.radius,
            // Рамка и затемнение — одной тенью: класс ring-* дал бы свою тень, и инлайн её перекрыл бы.
            boxShadow: '0 0 0 2px #f36a1c, 0 0 0 9999px rgba(8, 8, 10, 0.72)',
          }}
        />
      ) : (
        <div aria-hidden className="absolute inset-0 bg-[rgba(8,8,10,0.72)]" />
      )}
      <div
        ref={tipRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        className={`fixed w-[min(320px,calc(100vw-32px))] rounded-card border border-line bg-card p-4 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.6)] outline-none ${
          shown ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        style={tipPos ?? { top: 0, left: 0 }}
      >
        <div className="text-[12px] font-medium uppercase tracking-[0.14em] text-muted-2">
          {t('dashboard.tour.counter', { n: index + 1, total })}
        </div>
        <h2 id={titleId} className="mt-1.5 text-[16px] font-semibold leading-snug text-fg">
          {title}
        </h2>
        <p id={bodyId} className="mt-1 text-[14px] leading-[1.5] text-muted">
          {body}
        </p>
        <div className="mt-4 flex items-center justify-between gap-3">
          {last ? (
            <span />
          ) : (
            <button
              type="button"
              onClick={() => finish(false)}
              className="rounded-pill px-1 py-1 text-[14px] text-muted transition-colors hover:text-fg"
            >
              {t('dashboard.tour.skip')}
            </button>
          )}
          <Button size="sm" onClick={next}>
            {last ? t('dashboard.tour.done') : t('dashboard.tour.next')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
